import { Button, Card, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { IPreferredServiceOrganizationConsumer } from '../../../../interfaces/preferredServiceOrganizationConsumer';
import type { IServiceProvider } from '../../../../interfaces/serviceProvider';
import type { IPreferredServiceOrganizationConsumerWithServiceConsumers } from '../preferredServiceOrganizationConsumerWithServiceConsumers';
import { translateKey } from '../../../../../translate';
import { useAbortableFetch } from '../../../../../utils/hooks/useAbortableFetch';
import { SortDirection } from '../../../../../utils/sortDirection';
import { ConfirmationModal } from '../../../../elements/modals/ConfirmationModal';
import { ActionBarShowAndRemoveSelection } from '../../../../userDataManagement/ActionBarShowAndRemoveSelection';
import { PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal } from './Modals/PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal';
import { PersonalSettingsPreferenceListExaminationConsumersTable } from './PreferenceListExaminationTables/PersonalSettingsPreferenceListExaminationConsumersTable';

interface IPersonalSettingsPreferenceListExaminationSelectConsumersProps {
    isReadOnly: boolean;
    onDeleteOfLastConsumer?: () => void;
    serviceProviderId: string;
}

export function PersonalSettingsPreferenceListExaminationSelectConsumers(
    props: IPersonalSettingsPreferenceListExaminationSelectConsumersProps
) {
    const navigate = useNavigate();
    const abortableFetch = useAbortableFetch();
    const [serviceProvider, setServiceProvider] = useState<IServiceProvider>();
    const [preferredOrganizationConsumers, setPreferredOrganizationConsumers] = useState<
        IPreferredServiceOrganizationConsumerWithServiceConsumers[]
    >([]);
    const [selectedConsumerOrganizationIds, setSelectedConsumerOrganizationIds] = useState<string[]>([]);
    const [showAddOrganizationNationWideModal, setShowAddOrganizationNationWideModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [sortKey, setSortKey] = useState('organization.name');
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);

    useEffect(() => {
        loadServiceProvider();
    }, [props.serviceProviderId]);

    useEffect(() => {
        sortPreferredConsumers();
    }, [sortKey, sortDirection]);

    useEffect(() => {
        loadPreferredOrganizationsWithPractitioners();
    }, [serviceProvider?.id]);

    if (!serviceProvider) {
        return null;
    }

    return (
        <>
            <Card>
                <Card.Header
                    action={
                        !props.isReadOnly && (
                            <Button
                                iconLeft="add"
                                onClick={() => setShowAddOrganizationNationWideModal(true)}
                            >
                                {translateKey('organization.add')}
                            </Button>
                        )
                    }
                    title={`${serviceProvider.service.name} ${translateKey('episodeCategory.applicants')}`}
                />

                {!preferredOrganizationConsumers.length && (
                    <Card.Body>
                        <Text mb={8}>
                            {translateKey(
                                'personalSettings.preferenceList.addFavoriteOrganizations',
                                serviceProvider.service.name
                            )}
                        </Text>
                    </Card.Body>
                )}

                {preferredOrganizationConsumers.length > 0 && (
                    <PersonalSettingsPreferenceListExaminationConsumersTable
                        isReadOnly={props.isReadOnly}
                        onChange={async (selected) => setSelectedConsumerOrganizationIds(selected)}
                        onSort={handleSort}
                        selectedServiceOrganizationConsumersOrganizationIds={selectedConsumerOrganizationIds}
                        serviceOrganizationConsumers={preferredOrganizationConsumers}
                        sortDirection={sortDirection}
                        sortKey={sortKey}
                    />
                )}
            </Card>

            <PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal
                isOpen={showAddOrganizationNationWideModal}
                isReadOnly={props.isReadOnly}
                onAddOrganizations={handleAddOrganizations}
                onClose={async () => setShowAddOrganizationNationWideModal(false)}
                providerId={serviceProvider.id}
                serviceId={serviceProvider.service.id}
            />

            <ActionBarShowAndRemoveSelection
                handleClose={handleClose}
                handleDelete={handleDeleteSelectedPreferredOrganizations}
                isOpen={selectedConsumerOrganizationIds.length > 0}
                selectedIds={selectedConsumerOrganizationIds}
            />

            <ConfirmationModal
                cancelText={translateKey('personalSettings.preferenceList.deletePreferenceListCancelMessage')}
                confirmText={translateKey('personalSettings.preferenceList.deletePreferenceListConfirmMessage')}
                handleNo={handleModalNo}
                handleYes={handleModalYes}
                isOpen={showConfirmationModal}
                question={
                    <p>
                        {translateKey('personalSettings.preferenceList.deletePreferenceListMessageOne')}
                        <strong>
                            {translateKey('personalSettings.preferenceList.deletePreferenceListMessageTwo')}
                        </strong>
                    </p>
                }
                title={translateKey('personalSettings.preferenceList.deletePreferenceListTitle')}
            />
        </>
    );

    async function addPreferredServiceOrganizationConsumersForServiceProvider(
        serviceOrganizationConsumerOrganizationIds: string[],
        serviceProviderId: string
    ) {
        await Promise.all(
            serviceOrganizationConsumerOrganizationIds.map(async (serviceOrganizationConsumerOrganizationId) => {
                await abortableFetch.fetchJson('/api/preferredServiceOrganizationConsumers', 'POST', {
                    serviceProviderId: serviceProviderId,
                    organizationId: serviceOrganizationConsumerOrganizationId,
                });
            })
        );
    }

    async function handleAddOrganizations(serviceOrganizationConsumerOrganizationIds: string[]) {
        setShowAddOrganizationNationWideModal(false);
        await addPreferredServiceOrganizationConsumersForServiceProvider(
            serviceOrganizationConsumerOrganizationIds,
            props.serviceProviderId
        );
        await loadPreferredOrganizationsWithPractitioners();
    }

    async function loadServiceProvider() {
        const result = await abortableFetch.fetchJson(`/api/serviceProviders/${props.serviceProviderId}`, 'GET');
        const provider = result.json;
        setServiceProvider(provider);
    }

    async function handleSort(newSortKey: string, newSortDirection: SortDirection) {
        setSortKey(newSortKey);
        setSortDirection(newSortDirection);
    }

    function sortPreferredConsumers() {
        setPreferredOrganizationConsumers((current) => orderBy(current, sortKey, sortDirection));
    }

    async function loadPreferredOrganizationsWithPractitioners() {
        if (serviceProvider) {
            const result = await abortableFetch.fetchJson(
                `/api/preferredServiceOrganizationConsumers?isActive=true&serviceProviderId=${props.serviceProviderId}`,
                'GET'
            );
            const preferredOrganizations = result.json.items as IPreferredServiceOrganizationConsumer[];
            setPreferredOrganizationConsumers(await getPreferredOrganizationConsumers(preferredOrganizations));
            setSelectedConsumerOrganizationIds([]);
        }
    }

    async function getPreferredOrganizationConsumers(preferredOrganizations: IPreferredServiceOrganizationConsumer[]) {
        if (!serviceProvider) {
            return [];
        }

        const isActiveQueryParameter = props.isReadOnly ? '' : '&isActive=true';

        return await Promise.all(
            preferredOrganizations.map(async (preferredOrganization) => {
                const serviceConsumersUrl = `/api/serviceConsumers?organizationId=${preferredOrganization.organization.id}&serviceId=${serviceProvider.service.id}${isActiveQueryParameter}`;

                const serviceConsumers = (await abortableFetch.fetchJson(serviceConsumersUrl, 'GET')).json.items;

                return {
                    ...preferredOrganization,
                    serviceConsumers,
                };
            })
        );
    }

    async function handleModalYes() {
        setShowConfirmationModal(false);
        await deleteSelectedPreferredOrganizations();
        props.onDeleteOfLastConsumer?.();
        if (serviceProvider) {
            navigate(`/employments/${serviceProvider.employmentId}/personalSettings`);
        }
    }

    function handleModalNo() {
        setShowConfirmationModal(false);
    }

    async function handleDeleteSelectedPreferredOrganizations() {
        if (preferredOrganizationConsumers.length === selectedConsumerOrganizationIds.length) {
            setShowConfirmationModal(true);
        } else {
            deleteSelectedPreferredOrganizations();
        }
    }

    async function deleteSelectedPreferredOrganizations() {
        const filteredPreferredOrganizationConsumers = preferredOrganizationConsumers.filter((organization) =>
            selectedConsumerOrganizationIds.includes(organization.organization.id)
        );
        await abortableFetch.fetchJson('/api/preferredServiceOrganizationConsumers', 'DELETE', {
            preferredServiceOrganizationConsumerIds: filteredPreferredOrganizationConsumers.map(
                (preferredOrganizationConsumer) => preferredOrganizationConsumer.id
            ),
        });
        await loadPreferredOrganizationsWithPractitioners();
        setSelectedConsumerOrganizationIds([]);
    }

    async function handleClose() {
        setSelectedConsumerOrganizationIds([]);
    }
}
